import { useApolloClient } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import {
	EntityType,
	RecentSearch,
	RecentSearchInput
} from '@/core/graphql/generated/types';
import {
	getPendingView,
	useSetPendingView
} from '@/core/store/recentHistory/recentHistorySelectors';

import { useClearRecentHistoryMutation } from '../_graphql/mutation/ClearRecentHistory.hook';
import { useUpdateRecentProfilesMutation } from '../_graphql/mutation/UpdateRecentProfiles.hook';
import { useUpdateSearchHistoryMutation } from '../_graphql/mutation/UpdateSearchHistory.hook';
import {
	GetRecentHistoryDocument,
	GetRecentHistoryQuery,
	useGetRecentHistoryLazyQuery
} from '../_graphql/query/GetRecentHistory.hook';
import {
	ValidProfileVisit,
	SeachWithQueryString,
	isProfileVisit,
	isRecentSearch,
	isHcpHco
} from '../_types/QuickSearch.types';
import { buildRecentSearchQueryString } from '../_utils/buildRecentSearchQueryString';
import {
	handlePendingView,
	RESULTS_TO_SHOW
} from '../_utils/handlePendingView';

export const useRecentHistory = () => {
	const client = useApolloClient();
	const [clearRecentHistory] = useClearRecentHistoryMutation();
	const [updateRecentSearches] = useUpdateSearchHistoryMutation();
	const [updateRecentProfiles] = useUpdateRecentProfilesMutation();

	const pendingView = getPendingView();
	const setPendingView = useSetPendingView();

	const queryVariables = useMemo(
		() => ({
			paginationInput: {
				page: 1,
				pageSize: RESULTS_TO_SHOW + 1
			},
			entityTypes: [EntityType.Hco, EntityType.Hcp]
		}),
		[]
	);

	const [fetchRecentHistoryQuery, { data, loading }] =
		useGetRecentHistoryLazyQuery({
			fetchPolicy: 'cache-and-network'
		});

	const fetchRecentHistory = useCallback(
		() => fetchRecentHistoryQuery({ variables: queryVariables }),
		[fetchRecentHistoryQuery, queryVariables]
	);

	const clearHistory = useCallback(() => {
		clearRecentHistory({
			variables: {
				entityTypes: [EntityType.Hco, EntityType.Hcp]
			},
			update: cache => {
				cache.modify({
					fields: {
						recentProfiles: existing => {
							return {
								...existing,
								results: []
							};
						},
						recentSearches: existing => {
							return {
								...existing,
								results: []
							};
						}
					}
				});
			}
		});
	}, [clearRecentHistory]);

	const options = useMemo(() => {
		let profiles: ValidProfileVisit[] = [];
		let searches: SeachWithQueryString[] = [];

		if (data?.recentProfiles.__typename === 'ProfileVisitResults') {
			profiles = handlePendingView(
				data.recentProfiles.results.filter(result =>
					isHcpHco(result.profile)
				) as ValidProfileVisit[],
				pendingView
			) as ValidProfileVisit[];
		}

		if (data?.recentSearches.__typename === 'RecentSearchResults') {
			searches = (
				handlePendingView(
					data?.recentSearches.results,
					pendingView
				) as RecentSearch[]
			).map(
				search =>
					({
						...search,
						queryString: buildRecentSearchQueryString(search.payload)
					}) as SeachWithQueryString
			);
		}

		return [...profiles, ...searches];
	}, [data, pendingView]);

	const updateSearches = useCallback(
		(searchData: RecentSearchInput) => {
			let previousSearches: RecentSearch[] = [];

			const existingQueryData: GetRecentHistoryQuery | null = client.readQuery({
				query: GetRecentHistoryDocument,
				variables: queryVariables
			});

			if (
				existingQueryData?.recentSearches?.__typename === 'RecentSearchResults'
			) {
				previousSearches = existingQueryData.recentSearches.results;
			}

			updateRecentSearches({
				variables: {
					searchData
				},
				update: (_, { data }) => {
					if (isRecentSearch(data?.updateRecentSearches)) {
						const newSearchResult = {
							...data.updateRecentSearches,
							queryString: buildRecentSearchQueryString(
								data.updateRecentSearches.payload
							)
						} as SeachWithQueryString;

						setPendingView({
							pending: newSearchResult,
							previous:
								previousSearches.find(
									search =>
										isRecentSearch(search) &&
										search.searchKey === newSearchResult.searchKey &&
										search.entityType === newSearchResult.entityType
								) ?? null
						});
					}
				}
			});
		},
		[client, queryVariables, setPendingView, updateRecentSearches]
	);

	const updateProfiles = useCallback(
		(veevaId: string) => {
			let previousProfiles: ValidProfileVisit[] = [];

			const existingQueryData: GetRecentHistoryQuery | null = client.readQuery({
				query: GetRecentHistoryDocument,
				variables: queryVariables
			});

			if (
				existingQueryData?.recentProfiles?.__typename === 'ProfileVisitResults'
			) {
				previousProfiles = existingQueryData.recentProfiles.results.filter(
					p => p.profile.__typename !== 'ResourceNotFound'
				) as ValidProfileVisit[];
			}

			updateRecentProfiles({
				variables: {
					veevaId
				},
				update: (_, { data }) => {
					if (isProfileVisit(data?.updateRecentProfiles)) {
						const newProfileVisit = data?.updateRecentProfiles;

						if (isHcpHco(newProfileVisit.profile)) {
							const newVisit = newProfileVisit as ValidProfileVisit;

							setPendingView({
								pending: newVisit,
								previous:
									previousProfiles.find(
										profile =>
											isProfileVisit(profile) &&
											profile.profile.veevaid_V === newVisit.profile.veevaid_V
									) ?? null
							});
						}
					}
				}
			});
		},
		[client, queryVariables, updateRecentProfiles, setPendingView]
	);

	return {
		loading,
		options,
		fetchRecentHistory,
		updateSearches,
		updateProfiles,
		clearHistory
	};
};
